import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { SectionHeading } from './ui/SectionHeading';
import { previewPages } from '../data/previews';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = (index + total) % total;
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const childCenter = el.offsetLeft - track.offsetLeft + el.clientWidth / 2;
        const d = Math.abs(childCenter - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClasses = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-electric bg-surface text-foreground shadow-glow transition-colors duration-150 ease-out hover:bg-background focus:outline-none focus-visible:ring-4 focus-visible:ring-electric/50';
  return <section className="border-t border-electric/30 bg-background py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-94" data-mp="3qqpb457zxun1">
      <div className="mx-auto max-w-6xl px-4" data-id="element-95" data-mp="1oyx432vnbqyr">
        <div className="text-center" data-id="element-96" data-mp="1tsi5z60d1538">
          <p className="font-display text-base font-extrabold uppercase tracking-[.18em] text-gold" data-id="element-97" data-mp="1ttjhtcp79vm0">Look Inside</p>
          <SectionHeading id="inside-title" className="mt-3" data-id="element-98" data-mp="1f0ptr2vv475q">
            Discover What&apos;s Inside <span className="text-electric" data-id="element-99" data-mp="1jzassyzdoksl">INDUSTRIAL AUTOMATION &amp; PLC SYSTEMS — PROFESSIONAL EDITION™</span>
          </SectionHeading>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-muted" data-id="element-100" data-mp="2cqkp546887t4">
            Industrial automation fundamentals, PLC hardware and architecture, Ladder Logic, Boolean control principles,
            timers, counters, automated sequences, industrial sensors, actuators, digital and analog I/O, electric motors,
            variable frequency drives, HMI and SCADA systems, industrial communication protocols, Modbus, PROFINET,
            EtherNet/IP, PLC troubleshooting concepts, fault analysis, preventive maintenance, industrial safety, system
            reliability, Industry 4.0, and real-world industrial control scenarios.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages from the professional edition" data-id="element-101" data-mp="trms0hcn7627">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-4 pt-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-5 md:px-[4%] [&::-webkit-scrollbar]:hidden" data-id="element-102" data-mp="otlbo46lpkuf">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border-2 bg-surface transition-[border-color,opacity,box-shadow] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-electric opacity-100 shadow-glow-strong' : 'border-electric/25 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-103" data-mp="assgdh8h6lq9" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-104" data-mp="2crdyjktaarla" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClasses} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-105" data-mp="2gtsi674s1vdm">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-106" data-mp="2lq92srcrprly" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClasses} right-0 sm:-right-2`} aria-label="Next page" data-id="element-107" data-mp="2qkvi35lz04bb">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-108" data-mp="30ggmfaeihgo8" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-109" data-mp="34iboih49ny32">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-electric ${index === active ? 'w-6 bg-gold' : 'w-2 bg-electric/40 hover:bg-electric'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-110" data-mp="39i6dug8e8fm0" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}